/**
 * encoding helpers — one home for the "how do I read an encoding channel"
 * decisions that every chart's buildTraces would otherwise re-make inline.
 */

import type { DataPoint } from '../../types/dashboard';

/**
 * Resolve the x-axis values for a chart from its `encoding.xKey`.
 *
 * Single source of truth for the "absent xKey" decision, shared by every chart
 * that reads an x channel. When `xKey` is set, returns that column's values.
 * When it is absent, falls back — explicitly — to the row-index domain
 * (`[0, 1, 2, …]`) and warns in dev so the missing encoding surfaces instead of
 * failing silently.
 *
 * This centralizes a decision that used to drift per type: some modules
 * defaulted to `'' ` (→ `data.map(d => d[''])` → all `undefined` → Plotly's
 * silent ordinal auto-index), others to a magic `'x'` column name. Now there is
 * one behavior, defined here, and adding a chart type can't reintroduce either.
 */
export function xColumn(data: DataPoint[], xKey?: string): (string | number | null)[] {
  if (xKey) return data.map((d) => d[xKey]);
  if (import.meta.env.DEV) {
    console.warn(
      '[dashboard-kit] encoding.xKey is absent: the X axis falls back to the row index. ' +
        'Set encoding.xKey to name the X column.'
    );
  }
  return data.map((_, i) => i);
}
